import {
  ALMANAC_CATEGORY_DEFAULT,
  getWeedCategoryName,
  WEED_CATEGORY_BROADLEAF,
  WEED_CATEGORY_GRASS,
  WEED_CATEGORY_OFFSHOOT,
  WEED_CATEGORY_PURSLANE,
} from "./almanac";
import { capitalize } from "./strings";
import {
  CategoryClassification,
  categoryClassificationFromJSON,
  DiscriminatorConfig,
  DiscriminatorTypeCategory,
  Trust,
} from "protos/almanac/almanac";
import { DisplayCrop } from "./crops";
import { findWhere } from "./arrays";
import { keys } from "./objects";
import { TFunction } from "i18next";

export const DEFAULT_TRUST = Trust.fromPartial({
  avoid: false,
  ignorable: false,
});

export const DEFAULT_CATEGORY = DiscriminatorTypeCategory.fromPartial({
  type: {
    category: "",
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_CROP
    ),
  },
  trusts: [DEFAULT_TRUST, DEFAULT_TRUST, DEFAULT_TRUST],
});

export const DEFAULT_WEED_BROADLEAF = DiscriminatorTypeCategory.fromPartial({
  ...DEFAULT_CATEGORY,
  type: {
    category: WEED_CATEGORY_BROADLEAF,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
});

export const DEFAULT_WEED_GRASS = DiscriminatorTypeCategory.fromPartial({
  ...DEFAULT_CATEGORY,
  type: {
    category: WEED_CATEGORY_GRASS,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
});

export const DEFAULT_WEED_OFFSHOOT = DiscriminatorTypeCategory.fromPartial({
  ...DEFAULT_CATEGORY,
  type: {
    category: WEED_CATEGORY_OFFSHOOT,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
});

export const DEFAULT_WEED_PURSLANE = DiscriminatorTypeCategory.fromPartial({
  ...DEFAULT_CATEGORY,
  type: {
    category: WEED_CATEGORY_PURSLANE,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
});

export const DEFAULT_DISCRIMINATOR = DiscriminatorConfig.fromPartial({
  categories: [
    DEFAULT_WEED_BROADLEAF,
    DEFAULT_WEED_GRASS,
    DEFAULT_WEED_OFFSHOOT,
    DEFAULT_WEED_PURSLANE,
  ],
  protected: false,
});

export const getCategoryTitle = (
  t: TFunction,
  category: DiscriminatorTypeCategory,
  crops: DisplayCrop[] = []
): string => {
  if (!category.type?.category) {
    return t("models.weeds.categories.unknown");
  }
  if (category.type.category === ALMANAC_CATEGORY_DEFAULT) {
    return capitalize(t("utils.descriptors.default"));
  }

  const weedCatName = getWeedCategoryName(t, category.type.category);
  if (weedCatName) {
    return weedCatName;
  }

  const crop = findWhere(crops, { id: category.type.category });
  return crop?.localeName ?? t("models.crops.categories.unknown");
};

export const isCategorySynced = (
  category: DiscriminatorTypeCategory
): boolean => {
  for (const key of keys(DEFAULT_TRUST)) {
    const values = new Set();
    for (const trust of category.trusts) {
      values.add(trust[key]);
    }
    if (values.size > 1) {
      return false;
    }
  }
  return true;
};
